import { devices } from "constants/breakpoints";
import { useIsMobile } from "hooks";
import fourWayProtectionMobile from "assets/products/mlta/4way-protection-mobile.svg";
import fourWayProtectionWeb from "assets/products/mlta/4way-protection-web.svg";
import guaranteedAcceptanceMobile from "assets/products/mlta/guaranteed-acceptance-mobile.svg";
import guaranteedAcceptanceWeb from "assets/products/mlta/guaranteed-acceptance-web.svg";
import globalCoverageMobile from "assets/products/mlta/global-coverage-mobile.svg";
import globalCoverageWeb from "assets/products/mlta/global-coverage-web.svg";
import React from "react";
import MarketingGallery from "components/marketing/MarketingGallery";

interface HowAccidentInsuranceWorksProps {
    className?: string;
}

const HowAccidentInsuranceWorks = ({ className = "" }: HowAccidentInsuranceWorksProps) => {
    const isMobile = useIsMobile({ query: devices.tablet });

    const steps = [
        {
            icon: isMobile ? (
                <img src={fourWayProtectionMobile} alt="4WayProtection icon" className="w-20 h-20" />
            ) : (
                <img src={fourWayProtectionWeb} alt="4WayProtection icon" className="w-10 h-10" />
            ),
            title: "4-way protection",
            text: "Cash benefits for hospital stays, subsequent recovery, emergency room visits and loss of life for covered accidents.",
            ref: "⁷",
        },
        {
            icon: isMobile ? (
                <img src={guaranteedAcceptanceMobile} alt="Group icon" className="h-20 w-20" />
            ) : (
                <img src={guaranteedAcceptanceWeb} alt="Group icon" className="w-10 h-10" />
            ),
            title: "Simple acceptance",
            text: "Coverage is guaranteed for any AAA member ages 18-69, without any health questions or medical exams.",
            ref: "",
        },
        {
            icon: isMobile ? (
                <img src={globalCoverageMobile} alt="GlobalCoverage icon" className="w-20 h-20" />
            ) : (
                <img src={globalCoverageWeb} alt="GlobalCoverage icon" className="w-10 h-10" />
            ),
            title: "Coverage anytime, anywhere",
            text: "Accidents can occur anywhere in the world, and you're covered at home, at work, on the road or while traveling.",
            ref: "²",
        },
    ];

    return (
        <div className={`w-full flex items-start bg-neutral ${className}`} style={{ overflowX: "clip" }}>
            <div className="max-w-[1200px] mx-auto py-14">
                <div className="grid grid-cols-1 tablet:grid-cols-2 gap-2 tablet:gap-x-32 w-full">
                    {!isMobile && <MarketingGallery />}
                    <div className="space-y-6 px-11">
                        <h2 className="text-3xl tablet:text-5xl font-bold tablet:font-normal text-base-content text-center tablet:text-left relative">
                            <span className="block tablet:inline">How does accident </span>
                            <span className="block tablet:inline"> insurance work?</span>
                        </h2>

                        <div className="flex">
                            <div className="space-y-8">
                                {steps.map((step) => (
                                    <div key={step.title} className="flex space-x-6 text-base items-start">
                                        {!isMobile && (
                                            <div className="flex-shrink-0 flex items-center justify-center">{step.icon}</div>
                                        )}
                                        <div>
                                            {isMobile && (
                                                <div className="flex-shrink-0 flex items-center justify-center">
                                                    {step.icon}
                                                </div>
                                            )}
                                            <h3 className="text-2xl tablet:text-xl pb-2 font-semibold leading-8 tablet:leading-7 text-base-content text-center tablet:text-left">
                                                {step.title}
                                            </h3>
                                            <p className="text-base-content font-medium leading-6 font-sans text-base text-center tablet:text-left">
                                                {step.text}
                                                <a href="#disclosures" className="text-primary hover:underline">
                                                    <span className="text-blue-700 font-normal text-base">{step.ref}</span>
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {isMobile && <MarketingGallery />}
                </div>
            </div>
        </div>
    );
};

export default HowAccidentInsuranceWorks;
